import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class RequestLimiterService {
  private queue: Array<{
    url: string;
    headers?: HeadersInit;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }> = [];
  private activeCount = 0;
  private async _next(limit: number, retries: number) {
    if (this.queue.length === 0 || this.activeCount >= limit) {
      return;
    }

    const { url, headers, resolve, reject } = this.queue.shift()!;
    this.activeCount++;

    const reqHeaders = {
      ...headers,
      'X-Force-Strict-Transport-Security': 'true',
      'Strict-Transport-Security':
        'max-age=31536000; includeSubDomains; preload',
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
    };

    try {
      const response = await fetch(url, { headers: reqHeaders });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch ${url}: ${response.statusText || response.status}`,
        );
      }

      // Check content type and handle accordingly
      const contentType = response.headers.get('content-type');
      if (contentType?.toLowerCase().includes('application/json')) {
        const data = await response.json(); // Handle JSON response
        resolve(data);
      } else {
        const blob = await response.blob(); // Handle binary (blob) response like PDF
        resolve({
          body: blob,
          headers: response.headers,
          status: response.status,
        });
      }
    } catch (error) {
      if (retries > 0) {
        console.warn(
          `Retrying request for ${url}... (${retries} retries left)`,
        );
        retries--;
        this.queue.unshift({ url, headers, resolve, reject }); // Re-add request to the queue for retry
      } else {
        console.error(`Request to ${url} failed:`, error);
        reject({ url, error });
      }
    } finally {
      this.activeCount--;
      this._next(limit, retries); // Trigger the next request in the queue
    }
  }

  request(
    url: string,
    limit: number,
    retries: number,
    headers?: HeadersInit,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.queue.push({ url, headers, resolve, reject });
      this._next(limit, retries);
    });
  }

  async requestAll(
    urls: string[],
    limit: number,
    retries = 3,
    headers?: HeadersInit,
  ): Promise<{ success: any[]; failed: string[] }> {
    const results: any[] = [];
    const failed: string[] = [];

    const promises = urls.map((url) =>
      this.request(url, limit, retries, headers)
        .then((res) => {
          if (res) results.push(res);
        })
        .catch(() => failed.push(url)),
    );

    await Promise.allSettled(promises);
    return { success: results, failed };
  }

  async requestAllDoc(
    urls: any[],
    limit: number,
    retries = 3,
    headers?: HeadersInit,
  ): Promise<{ success: any[]; failed: string[] }> {
    const results: any[] = [];
    const failed: string[] = [];

    // Map over URLs to create promises and ensure they're returned
    const promises = urls.map((data: any) => {
      const fileTitle = data.file?.title ?? 'Unknown File';

      return this.request(data.url, limit, retries, headers)
        .then((res) => {
          if (res) {
            results.push({ ...res, title: fileTitle });
          }
        })
        .catch(() => {
          failed.push(fileTitle);
        });
    });

    // Wait for all promises to settle
    await Promise.allSettled(promises);

    return { success: results, failed };
  }
}
